/* eslint-disable @next/next/no-img-element */
/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { gsColors } from '../../../../styles/Variables'
import ArrowButton from '../../../ArrowButton'
import { useBooleanState, formatCurrency, mq, useIsDesktop, trackError, trackBookingFailed, totalServiceInstances, PLATFORM_PAY, notifyBugsnag } from '../../../../helpers'
import { ThreeDSError } from '../../../../helpers/threeDSecure'
import { reportStarbaseUnreachable, NETWORK_FAILURE_MESSAGE } from '../../../../helpers/networkDiagnostics'
import PlatformPayButton from '../../../checkout/PlatformPayButton'
import SelectedServices from './SelectedServices'
import { Header3 } from '../../../../styles/Components'

const BookingStepFooter = styled.footer`
  background-color: ${gsColors.white};
  box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.15);
  flex: 0 0 auto;
  position: fixed;
  opacity: 1;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;

  .container {
    opacity: 1;
    background-color: #FFF;
    margin: 0 auto;
    max-width: 1600px;

    ${mq('min', '768px')} {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .summary {
    height: 80px;
    position: relative;
    width: 500px;
    opacity: 1;
    background-color: #FFF;

    ${mq('max', '768px')} {
      height: unset;
      width: 100%;
      margin-right: 20px;

      &.open .cart-button {
        display: none;
      }
    }


    .cart-button {
      cursor: pointer;
      display: flex;
      align-items: center;
      border: none;
      background-color: #FFF;
      height: 100%;
      padding: 0 32px;
      z-index: 1000;
      
      img {
        margin-right: 12px;
      }

      ${mq('max', '768px')} {
        height: unset;
        padding: 16px 0;
        width: 100%;
        justify-content: center;

        h3 {
          font-size: 15px !important;
        }
      }
      
    }
    
    .cart {
      z-index: -500;
      box-shadow: 0px -10px 10px -10px rgba(0, 0, 0, 0.15),
        -10px 0px 10px -10px rgba(0, 0, 0, 0.15),
        10px 0px 10px -10px rgba(0, 0, 0, 0.15);
      position: absolute;
      bottom: -2000px;
      left: 20px;
      background-color: #FFF;
      min-width: 393px;
      transition: all .2s ease;
      
      .cart-inner {
        display: none;
      }

      ${mq('max', '768px')} {
        width: 95%;
        left: 50%;
        transform: translateX(-50%);
      }

      &.open {
        transition: all .2s ease;
        bottom: 80px;

        ${mq('max', '768px')} {
          bottom: 0;
        }

        .cart-inner {
          display: flex;
          z-index: 500;
        }
      }

    }
  }

  .appointment-total {
    display: flex;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.22;
    font-family: Euclid;
    justify-content: space-between;
    margin: 20px 15px;

    ${mq('min', '768px')} {
      font-size: 21px;
    }
  }
`


const BookingFlowFooter = ({
  state: { selectedAppointment: appointment, step, serviceRequests, market, selectedPaymentMethodId, platformPayAvailability },
  next,
  nextButtonLabel,
  nextButtonEnabled,
  deselectMarketService,
  dispatchValueAtKeyPath,
  platformPayConfirm
}) => {
  const isDesktop = useIsDesktop()

  const [summaryVisible, setSummaryVisibility] = useBooleanState()
  const [working, setWorking] = useState(false)

  useEffect(() => {
    /**
     * Close the cart drawer when the user clicks outside of it. Uses closest()
     * rather than walking a fixed number of parentElements, because the cart
     * markup nests service rows, add-on rows and the remove button several
     * levels below the drawer.
     */
    const clickAway = (e) => {
      const target = e.target

      // the toggle button and the in-drawer close button manage the drawer
      // themselves — bailing out here avoids double-toggling
      if (target?.closest?.('#cart-button') || target?.closest?.('#cart-drawer')) {
        return
      }

      // Remove buttons are checked by attribute rather than by ancestry: React
      // may have already unmounted the row by the time this bubbles to window,
      // and a detached node no longer matches closest('#cart-drawer').
      if (target?.closest?.('[data-cart-remove]')) {
        return
      }

      if (summaryVisible) {
        setSummaryVisibility()
      }
    }
    window.addEventListener('click', clickAway, { passive: true })

    return () => window.removeEventListener('click', clickAway)
  }, [summaryVisible, setSummaryVisibility])


  const submit = async () => {
    try {
      setWorking(true)
      dispatchValueAtKeyPath('bookingError', null)
      await next()
    } catch (err) {
      trackError('Booking Error', err)
      if (NETWORK_FAILURE_MESSAGE.test(err?.message || '')) {
        // the booking request itself never reached Starbase — capture why
        reportStarbaseUnreachable({ context: 'booking', error: err })
      } else if (!(err instanceof ThreeDSError || Array.isArray(err?.errors))) {
        // Card declines, 3DS outcomes and Starbase validation (the `errors` array
        // bookAppointment attaches) are expected outcomes already measured in
        // Segment. Anything else is a defect in the flow that trackError never
        // surfaced (it only reaches Segment, with the error serialised as {}),
        // which hid the "t is not a function" Book Now failures for weeks. Native
        // script errors are graded 'error'; the rest keep Bugsnag's 'warning'.
        const scriptError = err instanceof TypeError || err instanceof ReferenceError || err instanceof RangeError
        notifyBugsnag(
          err,
          {
            step,
            paymentMethod: selectedPaymentMethodId === PLATFORM_PAY ? 'platformPay' : 'card',
            market: market?.shortName,
            errorName: err?.name
          },
          'Booking',
          scriptError ? 'error' : undefined
        )
      }
      // step 5 is the payment/book-now step, so a failure here is a failed booking
      if (step === 5) {
        trackBookingFailed(
          err instanceof ThreeDSError ? `3dSecure_${err.reason}` : err.name || 'Error',
          err.message,
          appointment,
          market,
          selectedPaymentMethodId,
          platformPayAvailability,
        )
      }
      dispatchValueAtKeyPath('bookingError', err.message?.replace(/^.*?: /, '') || 'An error occurred')
    } finally {
      setWorking(false)
    }
  }

  const goToStep2 = () => {
    dispatchValueAtKeyPath('step', 2)
    setSummaryVisibility()
  }

  const isStepWithoutFooter = () => [1, 4, 6].includes(step)

  const hideOnDesktop = () => isDesktop && [1].includes(step)

  if (isStepWithoutFooter() || hideOnDesktop()) {
    return null
  }

  // The wallet button only replaces "Book Now" on step 5 — the only step it's mounted on.
  // Scope the ArrowButton-hide to that case, or a lingering PLATFORM_PAY selection would
  // hide the Next button on the OTHER footer steps (2, 3) after the user backtracks,
  // leaving them with no way to proceed.
  const showPlatformPayButton =
    step === 5 &&
    Number.isFinite(appointment?.finalPriceWithTaxAndTip) &&
    appointment.finalPriceWithTaxAndTip > 0
  const platformPaySelected = selectedPaymentMethodId === PLATFORM_PAY

  return (
    // The id lets the booking page measure the footer's height to keep the
    // Intercom launcher from overlapping it (see the vertical_padding effect in
    // pages/book/index.js).
    <BookingStepFooter id="booking-flow-footer">
      <div className="container">
        {/* container is justify-content: space-between; placeholder div keeps next button in place */}
        {!serviceRequests.length  && <div />}

        {!!serviceRequests.length && step < 5 && (
          <div className={`summary ${summaryVisible ? 'open' : ''}`}>
            <button className='cart-button' onClick={setSummaryVisibility} id='cart-button'>
              <img src='/images/book-calendar-icon.svg' alt='' aria-hidden='true' />
              <Header3
                fontSize={18}
                spacing={1.44}
                color={gsColors.sunsetOrange}
              >
                My Services ({totalServiceInstances(serviceRequests)})
              </Header3>
            </button>
            <div className={`cart ${summaryVisible ? 'open' :  ''}`} id='cart-drawer'>
              <div className='cart-inner'>
                <SelectedServices
                  {...{
                    step,
                    deselectMarketService,
                    goToStep2,
                    services: serviceRequests,
                    market,
                    onClose: setSummaryVisibility
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {step === 5 && (
          <div className="appointment-total">
            {isDesktop ? (
              <span>
                Total: {formatCurrency(appointment.finalPriceWithTaxAndTip)}
                {/* // TODO: Total is missing from availability response! */}
              </span>
            ) : (
              <>
                <span>Total</span>
                <span>
                  {formatCurrency(appointment.finalPriceWithTaxAndTip)}
                </span>
              </>
            )}
          </div>
        )}
        {/* Mounted at step 5 when a real total exists; hidden until platform pay is
            selected so onReady can report wallet availability for the dropdown. */}
        {showPlatformPayButton && (
          <PlatformPayButton
            amountDollars={appointment.finalPriceWithTaxAndTip}
            visible={platformPaySelected}
            onAvailabilityChange={availability =>
              dispatchValueAtKeyPath('platformPayAvailability', availability)
            }
            onConfirmBooking={paymentIntentId => platformPayConfirm(paymentIntentId)}
            onError={(message, errorType = 'payment') => {
              trackError('Booking Error', new Error(message))
              if (errorType === 'api' && NETWORK_FAILURE_MESSAGE.test(message || '')) {
                reportStarbaseUnreachable({ context: 'booking-platform-pay', error: message })
              }
              // errorType is the failure stage PlatformPayButton reports
              // ('payment' | 'api'); trackBookingFailed normalizes the
              // PLATFORM_PAY sentinel to paymentMethod: 'platformPay' so the
              // event matches the RN app's Booking Failed exactly.
              trackBookingFailed(errorType, message, appointment, market, PLATFORM_PAY, platformPayAvailability)
              dispatchValueAtKeyPath('bookingError', message)
            }}
          />
        )}

        {!(showPlatformPayButton && platformPaySelected) && (
          <ArrowButton
            footer
            disabled={!nextButtonEnabled || working}
            onClick={submit}
          >
            {nextButtonLabel}
          </ArrowButton>
        )}
      </div>
    </BookingStepFooter>
  )
}

export default BookingFlowFooter
